import { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { DashboardKpiItem } from '../models/DashboardKpiItem';
import { dashboardService } from '../services/dashboardService';
import { useAppContext } from '../contexts/Storage';

export const useDashboard = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [kpis, setKpis] = useState<DashboardKpiItem[]>([]);
  const { user, token } = useAppContext();

  const fetchDashboardKpis = useCallback(async () => {
    if (!user?.id) {
      setError('User not authenticated');
      toast.error('User not authenticated');
      return { success: false, error: 'User not authenticated' };
    }

    if (!token) {
      setError('No authentication token found');
      toast.error('No authentication token found');
      return { success: false, error: 'No authentication token found' };
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await dashboardService.getDashboardKpis(user.id, token);

      if (result.success && result.data) {
        // Check if data is an array
        if (Array.isArray(result.data)) {
          // Convert API data to DashboardKpiItem objects
          const kpiItems = result.data.map(item => DashboardKpiItem.fromJSON(item));
          setKpis(kpiItems);
          return { success: true, data: kpiItems };
        } else {
          // If data is not an array, handle as single object or empty array
          const errorMsg = 'Invalid data format received from server';
          setError(errorMsg);
          toast.error(errorMsg);
          return { success: false, error: errorMsg };
        }
      } else {
        setError(result.error || 'Failed to fetch dashboard KPIs');
        toast.error(result.error || 'Failed to fetch dashboard KPIs');
        return { success: false, error: result.error };
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  }, [user, token]);

  return {
    kpis,
    isLoading,
    error,
    fetchDashboardKpis,
    setKpis
  };
};